import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileBadge, Plus, ArrowUpRight, AlertTriangle } from "lucide-react";

const STATUS_STYLES = {
  "Not Started": "bg-slate-100 text-slate-600", "Data Review": "bg-slate-100 text-slate-700",
  "Drafting Statements": "bg-blue-100 text-blue-800", "ComplyX Review": "bg-indigo-100 text-indigo-800",
  "Needs Revision": "bg-amber-100 text-amber-800", "Ready for Approval": "bg-orange-100 text-orange-800",
  "Approved": "bg-emerald-100 text-emerald-800", "Superseded": "bg-slate-200 text-slate-600",
  "Archived": "bg-slate-100 text-slate-500", "Entered in Error": "bg-red-100 text-red-700",
};
const CARD_ORDER = ["In Progress", "Needs Information", "Ready for Review", "Approved", "Needs Update"];

export default function SSPDashboard() {
  const nav = useNavigate();
  const [cards, setCards] = useState({});
  const [docs, setDocs] = useState([]);

  const load = useCallback(() => {
    api.get("/ssp/dashboard").then((r) => { setCards(r.data.cards || {}); setDocs(r.data.documents || []); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6 flex items-start justify-between">
        <div>
          <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2"><FileBadge className="h-6 w-6 text-gold" /> SSP Builder</h1>
          <p className="text-slate-300 text-sm mt-2 max-w-3xl">Generate reusable, customer-specific CMMC Level 2 System Security Plans from the ComplyX Gov master template using existing Command Center data.</p>
        </div>
        <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={() => nav("/operations/ssp/new")} data-testid="ssp-new-btn"><Plus className="h-4 w-4 mr-1.5" /> New SSP</Button>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ssp-cards">
          {CARD_ORDER.map((k) => (
            <div key={k} className="rounded-md border border-border bg-card p-4" data-testid={`ssp-card-${k.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="text-3xl font-display font-bold text-navy">{cards[k] || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">{k}</div>
            </div>
          ))}
        </div>

        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b text-left">{["Customer", "Ver", "Status", "Reviewed", "Impl", "Planned", "N/A", "Manual", "Reviewer", "Updated", ""].map((h) => <th key={h} className="px-3 py-2.5 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {docs.length === 0 ? <tr><td colSpan={11} className="px-3 py-10 text-center text-muted-foreground text-sm">No SSPs yet. Click <b>New SSP</b> to build one.</td></tr> :
                docs.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/operations/ssp/${d.id}/controls`)} data-testid="ssp-row">
                    <td className="px-3 py-2.5 font-medium text-navy">{d.customer_name} {d.needs_update && <span title="SSP Update Required" className="inline-flex items-center gap-1 text-[10px] text-amber-700"><AlertTriangle className="h-3 w-3" />Update</span>}</td>
                    <td className="px-3 py-2.5 text-xs">{d.version}</td>
                    <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[d.status] || "bg-slate-100"}`}>{d.status}</span></td>
                    <td className="px-3 py-2.5 text-xs">{d.controls_reviewed}/110</td>
                    <td className="px-3 py-2.5 text-xs text-emerald-700">{d.implemented}</td>
                    <td className="px-3 py-2.5 text-xs text-blue-700">{d.planned}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{d.not_applicable}</td>
                    <td className="px-3 py-2.5 text-xs text-amber-700">{d.needs_manual_review}</td>
                    <td className="px-3 py-2.5 text-xs">{d.assigned_reviewer || "—"}</td>
                    <td className="px-3 py-2.5 text-xs">{String(d.updated_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2.5"><ArrowUpRight className="h-4 w-4 text-slate-400" /></td>
                  </tr>
                ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
